<!-- Hero -->
<section
  class="relative flex min-h-[calc(100svh-4.25rem)] items-end overflow-hidden bg-ink sm:min-h-[36rem] lg:min-h-[40rem]"
>
  <img
    src="assets/marketing/secure-trade-hero.webp"
    alt="A leather saddle on a fence beside a long Australian country road at golden hour"
    fetchpriority="high"
    class="absolute inset-0 h-full w-full object-cover object-[58%_40%]"
  />
  <div
    class="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/10 sm:bg-gradient-to-r sm:from-ink/85 sm:via-ink/40 sm:to-transparent"
  ></div>
  <div
    class="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent sm:hidden"
  ></div>
  <div class="hero-grain absolute inset-0 opacity-[0.04] mix-blend-overlay"></div>

  <div class="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-28 sm:px-6 sm:pb-20 sm:pt-32 lg:px-8">
    <div class="max-w-xl">
      <p class="text-sm font-medium uppercase tracking-wide text-gold">Secure Trade</p>
      <h1
        class="mt-4 font-display text-4xl font-semibold text-cream sm:text-5xl lg:text-[3.25rem] lg:leading-tight"
      >
        The right saddle, from anywhere in Australia.
      </h1>
      <p class="mt-5 max-w-md text-lg text-cream/80">
        Found it on Facebook Marketplace or a noticeboard? Bring the deal onto Galopa — we hold the
        payment until it arrives as described.
      </p>

      <div class="mt-9 flex flex-wrap items-center gap-4">
        <a
          routerLink="/account/secure-trade/new"
          class="rounded-full bg-cream px-6 py-3 text-sm font-medium text-forest transition hover:bg-tanlight"
        >
          Start a secure trade
        </a>
        <a
          href="/secure-trade#how-it-works"
          class="rounded-full border border-cream/40 px-6 py-3 text-sm font-medium text-cream transition hover:border-cream"
          (click)="scrollToHowItWorks($event)"
        >
          See how it works
        </a>
      </div>

      <div class="mt-6 text-xs text-cream/60">
        <app-stripe-badge label="Powered by Stripe - secured payments" />
      </div>
    </div>
  </div>
</section>

<!-- Trust strip -->
<section class="border-t border-cream/10 bg-forestdark text-cream">
  <div
    class="mx-auto grid max-w-7xl grid-cols-2 gap-x-4 gap-y-8 px-4 py-10 sm:gap-6 sm:px-6 sm:py-8 lg:grid-cols-4 lg:gap-8 lg:px-8"
  >
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="lock" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Payment held safely
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Until you say it's good</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="eye" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Inspect before it releases
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Confirm it matches the listing</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="truck" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Ships Australia-wide
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Tracked, not just local pickup</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="shield" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Real people, verified
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Identity-checked payouts</p>
      </div>
    </div>
  </div>

  <div class="border-t border-cream/10 px-4 py-5 sm:px-6 lg:px-8">
    <div
      class="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-2 text-center text-xs text-cream/55"
    >
      <span class="flex items-center gap-1.5">
        <app-icon name="lock" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
        256-bit SSL encryption
      </span>
      <span class="flex items-center gap-1.5">
        <app-icon name="shield" svgClass="h-3.5 w-3.5" [strokeWidth]="2" />
        PCI-DSS Level 1 certified
      </span>
      <app-stripe-badge label="Payments processed by Stripe" markClass="text-[#8b91ff]" />
    </div>
  </div>
</section>

<!-- Explainer film -->
<section class="bg-ink px-4 py-16 sm:px-6 sm:py-20 lg:px-8" appReveal>
  <div class="mx-auto max-w-6xl">
    <div class="mx-auto max-w-2xl text-center">
      <p class="text-sm font-medium uppercase tracking-wide text-gold">Watch it play out</p>
      <h2 class="mt-3 font-display text-3xl font-semibold text-cream sm:text-4xl">
        From a Facebook listing to a saddle in your tack room.
      </h2>
      <p class="mt-4 text-cream/70">
        The trust Marketplace never built in — held funds, identity-checked payouts, and a country
        big enough to actually find the right tree width.
      </p>
    </div>
    <div class="mt-10">
      <app-secure-trade-explainer />
    </div>
  </div>
</section>

<!-- Australia-wide infographic -->
<section class="bg-creamdark/50 px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
    <div>
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">Interstate, on purpose</p>
      <h2 class="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl">
        The paddock is the whole country.
      </h2>
      <p class="mt-4 text-ink/70">
        Facebook Marketplace is built for the suburb next door. A Bates 17" in Perth is useless to a
        rider in Brisbane if the only way to pay is cash on the driveway. Secure Trade is how that
        saddle actually moves — payment locked, courier tracked, inspected on arrival.
      </p>

      <dl class="mt-8 grid grid-cols-2 gap-4">
        <div class="rounded-2xl border border-line bg-white px-4 py-4">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-ink/45">Without it</dt>
          <dd class="mt-1 font-display text-xl font-semibold text-ink">Local only</dd>
          <p class="mt-1 text-xs text-ink/55">Whoever happens to be selling nearby</p>
        </div>
        <div class="rounded-2xl border border-forest/15 bg-forest px-4 py-4 text-cream">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-gold">
            With Secure Trade
          </dt>
          <dd class="mt-1 font-display text-xl font-semibold text-cream">
            8 states &amp; territories
          </dd>
          <p class="mt-1 text-xs text-cream/65">The whole market, not the neighbourhood</p>
        </div>
      </dl>
    </div>

    <div class="overflow-hidden rounded-3xl border border-line bg-white p-5 sm:p-8">
      <p class="mb-4 text-center text-[11px] font-medium uppercase tracking-[0.16em] text-tandark">
        Coast to coast
      </p>
      <svg
        viewBox="0 0 430 370"
        class="mx-auto w-full max-w-lg text-ink"
        role="img"
        aria-labelledby="au-map-title"
      >
        <title id="au-map-title">Map of Australia showing interstate Secure Trade routes</title>
        <path
          class="fill-creamdark stroke-line"
          stroke-width="1.4"
          stroke-linejoin="round"
          [attr.d]="australiaMainlandPath"
        />
        <path
          class="fill-creamdark stroke-line"
          stroke-width="1.4"
          stroke-linejoin="round"
          [attr.d]="australiaTasmaniaPath"
        />

        <path
          class="route-line fill-none stroke-gold"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M52 235 C 140 180, 250 150, 364 192"
        />
        <path
          class="route-line route-line-slow fill-none stroke-gold"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M52 235 C 140 280, 250 290, 349 254"
        />
        <path
          class="route-line fill-none stroke-gold"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M178 46 C 200 140, 240 220, 296 292"
        />
        <path
          class="route-line route-line-slow fill-none stroke-gold"
          stroke-width="1.6"
          stroke-linecap="round"
          d="M364 192 C 370 250, 340 310, 316 341"
        />

        @for (city of cities; track city.name) {
          <circle [attr.cx]="city.x" [attr.cy]="city.y" r="4.5" class="fill-gold/35" />
          <circle [attr.cx]="city.x" [attr.cy]="city.y" r="2.6" class="fill-forest" />
          <text
            [attr.x]="city.x + (city.anchor === 'start' ? 8 : city.anchor === 'end' ? -8 : 0)"
            [attr.y]="city.y - 8"
            [attr.text-anchor]="city.anchor"
            fill="currentColor"
            font-size="11"
            font-weight="500"
            font-family="inherit"
          >
            {{ city.name }}
          </text>
        }
      </svg>
      <p class="mt-4 text-center text-xs text-ink/50">
        Perth → Brisbane &middot; Darwin → Melbourne &middot; Hobart → Sydney
      </p>
    </div>
  </div>
</section>

<!-- Marketplace vs Galopa -->
<section class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto max-w-6xl">
    <div class="mx-auto max-w-2xl text-center">
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">The missing piece</p>
      <h2 class="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl">
        Same saddle. Completely different deal.
      </h2>
      <p class="mt-4 text-ink/70">
        You already found it on Marketplace. Secure Trade is the trust layer that listing never had.
      </p>
    </div>

    <div
      class="mt-12 grid grid-cols-1 overflow-hidden rounded-3xl border border-line lg:grid-cols-2"
    >
      <div class="bg-creamdark/60 p-7 sm:p-10">
        <p class="text-xs font-medium uppercase tracking-wide text-ink/45">Facebook Marketplace</p>
        <h3 class="mt-2 font-display text-2xl font-semibold text-ink">Pay a stranger. Hope.</h3>
        <ul class="mt-6 space-y-4 text-sm text-ink/70">
          <li class="flex gap-3">
            <app-icon name="x" svgClass="mt-0.5 h-4 w-4 shrink-0 text-ink/35" [strokeWidth]="2" />
            Cash, bank transfer, or a driveway handshake — no one holds the money
          </li>
          <li class="flex gap-3">
            <app-icon name="x" svgClass="mt-0.5 h-4 w-4 shrink-0 text-ink/35" [strokeWidth]="2" />
            Local only. Interstate usually means “too hard”
          </li>
          <li class="flex gap-3">
            <app-icon name="x" svgClass="mt-0.5 h-4 w-4 shrink-0 text-ink/35" [strokeWidth]="2" />
            No inspection window. No ID. No recourse if it isn't as described
          </li>
        </ul>
      </div>
      <div class="relative overflow-hidden bg-forest p-7 text-cream sm:p-10">
        <img
          src="assets/marketing/secure-trade-inspect.webp"
          alt=""
          class="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div class="relative">
          <p class="text-xs font-medium uppercase tracking-wide text-gold">Galopa Secure Trade</p>
          <h3 class="mt-2 font-display text-2xl font-semibold text-cream">
            Hold the payment. Check the saddle.
          </h3>
          <ul class="mt-6 space-y-4 text-sm text-cream/80">
            <li class="flex gap-3">
              <app-icon
                name="check"
                svgClass="mt-0.5 h-4 w-4 shrink-0 text-gold"
                [strokeWidth]="2"
              />
              Funds sit with Galopa until you confirm it's as described
            </li>
            <li class="flex gap-3">
              <app-icon
                name="check"
                svgClass="mt-0.5 h-4 w-4 shrink-0 text-gold"
                [strokeWidth]="2"
              />
              Ships Australia-wide — tracked, not left on a porch
            </li>
            <li class="flex gap-3">
              <app-icon
                name="check"
                svgClass="mt-0.5 h-4 w-4 shrink-0 text-gold"
                [strokeWidth]="2"
              />
              Identity-checked payouts. Flag an issue and nothing releases
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- How it works -->
<section id="how-it-works" class="bg-creamdark/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto max-w-6xl">
    <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
      How it works
    </p>
    <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
      Try it, check it, approve it.
    </h2>
    <p class="mx-auto mt-4 max-w-2xl text-center text-ink/70">
      Either of you can start it — the buyer or the seller. From there, your money doesn't move
      until the saddle does.
    </p>

    <div class="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      @for (step of steps; track step.n) {
        <article class="overflow-hidden rounded-3xl border border-line bg-white">
          <div class="aspect-[4/3] overflow-hidden bg-creamdark">
            <img
              [src]="step.img"
              [alt]="step.alt"
              loading="lazy"
              class="h-full w-full object-cover"
            />
          </div>
          <div class="p-5">
            <span
              class="flex h-8 w-8 items-center justify-center rounded-full bg-forest font-display text-sm font-semibold text-cream"
              >{{ step.n }}</span
            >
            <p class="mb-1 mt-3 font-semibold text-ink">{{ step.title }}</p>
            <p class="text-sm leading-relaxed text-ink/70">{{ step.body }}</p>
          </div>
        </article>
      }
    </div>
  </div>
</section>

<!-- Payment held infographic -->
<section class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto max-w-6xl">
    <div class="mx-auto max-w-2xl text-center">
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">Where the money sits</p>
      <h2 class="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl">
        Nobody is asked to go first on trust.
      </h2>
    </div>

    <div class="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-center">
      <div class="rounded-3xl border border-line bg-white p-6 text-center sm:p-8">
        <span
          class="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-creamdark text-tandark"
        >
          <app-icon name="user" svgClass="h-5 w-5" [strokeWidth]="1.75" />
        </span>
        <p class="mt-4 font-display text-xl font-semibold text-ink">Buyer pays in</p>
        <p class="mt-2 text-sm text-ink/65">
          Card charged. Funds leave the buyer — not into the seller's pocket.
        </p>
      </div>
      <app-icon
        name="arrowRight"
        svgClass="mx-auto hidden h-6 w-6 text-tan lg:block"
        [strokeWidth]="1.75"
      />
      <div class="rounded-3xl border border-forest/20 bg-forest p-6 text-center text-cream sm:p-8">
        <span
          class="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-cream/15 text-gold"
        >
          <app-icon name="lock" svgClass="h-5 w-5" [strokeWidth]="1.75" />
        </span>
        <p class="mt-4 font-display text-xl font-semibold text-cream">Galopa holds it</p>
        <p class="mt-2 text-sm text-cream/70">
          Locked until the saddle is in hand and confirmed as described.
        </p>
      </div>
      <app-icon
        name="arrowRight"
        svgClass="mx-auto hidden h-6 w-6 text-tan lg:block"
        [strokeWidth]="1.75"
      />
      <div class="rounded-3xl border border-line bg-white p-6 text-center sm:p-8">
        <span
          class="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-creamdark text-tandark"
        >
          <app-icon name="check" svgClass="h-5 w-5" [strokeWidth]="1.75" />
        </span>
        <p class="mt-4 font-display text-xl font-semibold text-ink">Seller is paid</p>
        <p class="mt-2 text-sm text-ink/65">
          Released only after you approve — or if you flag an issue, it stays held.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- Buyer + seller -->
<section class="bg-creamdark/50 px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div class="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-2">
    <div class="overflow-hidden rounded-3xl border border-line bg-white">
      <div class="aspect-[16/9] overflow-hidden">
        <img
          src="assets/marketing/secure-trade-inspect.webp"
          alt="Inspecting a saddle in a tack room before releasing payment"
          loading="lazy"
          class="h-full w-full object-cover"
        />
      </div>
      <div class="p-8 sm:p-10">
        <span
          class="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest"
        >
          <app-icon name="eye" svgClass="h-5 w-5" [strokeWidth]="2" />
        </span>
        <h3 class="mt-5 font-display text-2xl font-semibold text-ink">If you're buying</h3>
        <p class="mt-3 text-ink/70">
          You're about to send real money for an expensive piece of tack you've only seen in photos.
          Payment stays held until the saddle meets your standards — it isn't released to the seller
          until you've had it in hand and confirmed it's the real deal.
        </p>
      </div>
    </div>
    <div class="overflow-hidden rounded-3xl border border-line bg-white">
      <div class="aspect-[16/9] overflow-hidden">
        <img
          src="assets/marketing/secure-trade-pack.webp"
          alt="Packing a leather saddle for interstate shipping"
          loading="lazy"
          class="h-full w-full object-cover"
        />
      </div>
      <div class="p-8 sm:p-10">
        <span
          class="flex h-11 w-11 items-center justify-center rounded-full bg-forest/10 text-forest"
        >
          <app-icon name="truck" svgClass="h-5 w-5" [strokeWidth]="2" />
        </span>
        <h3 class="mt-5 font-display text-2xl font-semibold text-ink">If you're selling</h3>
        <p class="mt-3 text-ink/70">
          You don't want to post something that bulky and valuable on a stranger's promise. Once the
          buyer pays in, the funds are real and held with Galopa before you ever box it up — so you
          ship knowing you'll get paid.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- Highway panel -->
<section class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div
    class="relative mx-auto min-h-[22rem] max-w-6xl overflow-hidden rounded-3xl border border-line sm:min-h-[28rem]"
  >
    <img
      src="assets/marketing/secure-trade-highway.webp"
      alt="A courier van on a long rural Australian highway at golden hour"
      loading="lazy"
      class="absolute inset-0 h-full w-full object-cover"
    />
    <div class="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/70 to-ink/30"></div>
    <div class="relative max-w-lg px-8 py-14 sm:px-12 sm:py-20">
      <p class="text-sm font-medium uppercase tracking-wide text-gold">Tracked, not hoped-for</p>
      <h2 class="mt-3 font-display text-2xl font-semibold text-cream sm:text-3xl">
        A four-thousand-kilometre driveway.
      </h2>
      <p class="mt-4 text-cream/75">
        Galopa already verifies sellers and protects payments for saddles listed here. Secure Trade
        brings that same protection to a deal you found anywhere else — same trust, same team, no
        separate app.
      </p>
      <a
        routerLink="/account/secure-trade/new"
        class="mt-7 inline-flex items-center gap-1.5 rounded-full bg-cream px-6 py-3 text-sm font-medium text-forest transition hover:bg-tanlight"
      >
        Start a secure trade
        <app-icon name="arrowRight" svgClass="h-4 w-4" [strokeWidth]="2" />
      </a>
    </div>
  </div>
</section>

<!-- FAQ -->
<section class="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">Questions</p>
  <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
    Before you start a trade
  </h2>

  <dl class="mt-12 space-y-8">
    <div>
      <dt class="flex items-center gap-2 font-semibold text-ink">
        <app-icon name="lock" svgClass="h-4 w-4 text-tandark" [strokeWidth]="2" />
        Is my card information safe?
      </dt>
      <dd class="mt-2 text-ink/70">
        Yes. Payments are entered directly into
        <span class="font-semibold text-[#635bff]">Stripe</span>'s encrypted, PCI-DSS Level 1
        certified checkout — the same processor used by millions of businesses worldwide. Your card
        number is encrypted end-to-end and never touches Galopa's servers.
      </dd>
    </div>
    <div>
      <dt class="font-semibold text-ink">Who can I trade with?</dt>
      <dd class="mt-2 text-ink/70">
        Anyone, anywhere in Australia — someone you found on Facebook Marketplace, a saddlery
        noticeboard, or word of mouth. They just need an email address; we'll invite them and
        they'll set up a free Galopa account to accept.
      </dd>
    </div>
    <div>
      <dt class="font-semibold text-ink">What if the saddle isn't as described?</dt>
      <dd class="mt-2 text-ink/70">
        Don't approve it — flag the issue instead. The payment stays held while it's sorted out, and
        if the seller agrees it's not right, you're refunded.
      </dd>
    </div>
    <div>
      <dt class="font-semibold text-ink">What does it cost?</dt>
      <dd class="mt-2 text-ink/70">
        A small, fixed fee on top of the sale price for the buyer (3%), and a fee deducted from the
        payout for the seller (5%) — you'll see the exact numbers before you pay a cent.
      </dd>
    </div>
    <div>
      <dt class="font-semibold text-ink">How long does it take?</dt>
      <dd class="mt-2 text-ink/70">
        As long as shipping takes, plus time to check it over. If a buyer doesn't respond within 7
        days of the saddle shipping, the trade completes automatically so a seller is never left
        waiting indefinitely.
      </dd>
    </div>
  </dl>
</section>

<!-- Final CTA -->
<section class="relative overflow-hidden bg-forest py-20 text-cream sm:py-24">
  <img
    src="assets/marketing/secure-trade-hero.webp"
    alt=""
    class="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25"
  />
  <div class="absolute inset-0 bg-forest/70"></div>
  <div class="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
    <h2 class="font-display text-3xl font-semibold text-cream sm:text-4xl">
      No leap of faith required.
    </h2>
    <p class="mt-4 text-cream/75">
      You hold the payment key — start a trade in a couple of minutes.
    </p>
    <a
      routerLink="/account/secure-trade/new"
      class="mt-8 inline-block rounded-full bg-cream px-8 py-3.5 text-sm font-medium text-forest transition hover:bg-tanlight"
    >
      Start a secure trade
    </a>
  </div>
</section>
